import { ViewConfig } from "@lib/views";
import { AMOUNT_COLUMN_SIZE } from "@design-system/ViewTable/DefaultColumnSizes";
import { EliminationExceptionReason } from "@common/types/domains/accounting/elimination";
import { EliminationDetailJournalEntry } from "@common/schemas/domains/accounting/elimination.schema";
import { ViewTableMeta } from "@hooks/view/useViewTableMeta";
import { GlDrillDownParams } from "../glDrillDown.lib";
import {
    ExceptionAccountCell,
    ExceptionAmountCell,
    ExceptionActionsCell,
    ExceptionCounterpartyCell,
    ExceptionCounterpartyEntityCell,
    ExceptionDateCell,
    ExceptionDescriptionCell,
    ExceptionEntityCell,
    ExceptionFactTypeCell,
    ExceptionReasonCell,
    ExceptionReferenceCell,
    ExceptionSourceCell,
} from "./eliminationExceptionsCells";

export enum EliminationExceptionsColumn {
    ENTITY = "entity",
    COUNTERPARTY_ENTITY = "counterpartyEntity",
    COUNTERPARTY = "counterparty",
    DATE = "date",
    REFERENCE = "reference",
    DESCRIPTION = "description",
    ACCOUNT = "account",
    SOURCE = "source",
    TYPE = "type",
    REASON = "reason",
    AMOUNT = "amount",
    ACTIONS = "actions",
}

export interface EliminationExceptionsViewRow {
    id: string;
    reason: EliminationExceptionReason;
    reasonLabel: string;
    // in_transit_transfer is informational, not an actionable imbalance — the
    // table de-emphasises these rows.
    isInformational: boolean;
    entityName: string;
    // The paired entity the exception is against (the other side of the pair).
    counterpartyEntityName: string;
    // The transaction counterparty (vendor/customer) on the provenance lines,
    // distinct from the paired entity.
    counterpartyName: string;
    date: string | null;
    description: string;
    journalEntries: EliminationDetailJournalEntry[];
    accountLabel: string;
    sourceLabel: string;
    // Balance sheet / P&L (or opening balance / equity) categorisation.
    factTypeLabel: string;
    netAmount: number;
    // Drill-down inputs (resolved upstream so cells open GL with one click).
    startDate?: Date;
    endDate?: Date;
    linkEntityId: number;
    linkAccountCode?: number;
    transactionIds: number[];
}

export const eliminationExceptionsViewConfig: ViewConfig<
    EliminationExceptionsViewRow,
    EliminationExceptionsColumn,
    never
> = {
    columns: {
        [EliminationExceptionsColumn.ENTITY]: {
            accessorKey: "entityName",
            name: "Entity",
            header: "Entity",
            cell: ExceptionEntityCell,
            minSize: 140,
            maxSize: 220,
        },
        [EliminationExceptionsColumn.COUNTERPARTY_ENTITY]: {
            accessorKey: "counterpartyEntityName",
            name: "Counterparty entity",
            header: "Counterparty entity",
            cell: ExceptionCounterpartyEntityCell,
            minSize: 140,
            maxSize: 220,
        },
        [EliminationExceptionsColumn.COUNTERPARTY]: {
            accessorKey: "counterpartyName",
            name: "Counterparty",
            header: "Counterparty",
            cell: ExceptionCounterpartyCell,
            minSize: 140,
            maxSize: 220,
        },
        [EliminationExceptionsColumn.DATE]: {
            accessorKey: "date",
            name: "Date",
            header: "Date",
            cell: ExceptionDateCell,
            minSize: 110,
            maxSize: 160,
        },
        [EliminationExceptionsColumn.REFERENCE]: {
            accessorKey: "journalEntries",
            name: "Reference",
            header: "Reference",
            cell: ExceptionReferenceCell,
            minSize: 160,
            maxSize: 260,
        },
        [EliminationExceptionsColumn.DESCRIPTION]: {
            accessorKey: "description",
            name: "Description",
            header: "Description",
            cell: ExceptionDescriptionCell,
            minSize: 200,
            maxSize: 360,
        },
        [EliminationExceptionsColumn.ACCOUNT]: {
            accessorKey: "accountLabel",
            name: "Account",
            header: "Account",
            cell: ExceptionAccountCell,
            minSize: 220,
            maxSize: 400,
        },
        [EliminationExceptionsColumn.SOURCE]: {
            accessorKey: "sourceLabel",
            name: "Source",
            header: "Source",
            cell: ExceptionSourceCell,
            minSize: 110,
            maxSize: 160,
        },
        [EliminationExceptionsColumn.TYPE]: {
            accessorKey: "factTypeLabel",
            name: "Type",
            header: "Type",
            cell: ExceptionFactTypeCell,
            minSize: 120,
            maxSize: 180,
        },
        [EliminationExceptionsColumn.REASON]: {
            accessorKey: "reasonLabel",
            name: "Reason",
            header: "Reason",
            cell: ExceptionReasonCell,
            minSize: 160,
            maxSize: 260,
        },
        [EliminationExceptionsColumn.AMOUNT]: {
            accessorKey: "netAmount",
            name: "Unmatched amount",
            header: "Unmatched amount",
            cell: ExceptionAmountCell,
            ...AMOUNT_COLUMN_SIZE,
        },
        [EliminationExceptionsColumn.ACTIONS]: {
            accessorKey: "id",
            name: "Actions",
            header: "",
            cell: ExceptionActionsCell,
            minSize: 48,
            maxSize: 64,
            immutable: true,
            hiddenFromSettings: true,
        },
    },
};

export type EliminationExceptionsTableMeta = ViewTableMeta<
    {
        onCreateJournalEntry: (row: EliminationExceptionsViewRow) => void;
        onCreateEliminatingJournalEntry: (
            row: EliminationExceptionsViewRow,
        ) => void;
        onDrillDown: (params: GlDrillDownParams) => void;
    },
    unknown
>;
